import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Platforms from './Platforms';
import ConnectedPlatforms from './ConnectedPlatforms';
import { useAppSelector } from 'src/redux/storeHooks';
import { useAppDispatch } from 'src/redux/storeHooks'
import { fetchPlatformsAsync } from 'src/redux/actions/platformActions';

const getChannelDisplayLabel = (value: string) => {
  const normalized = `${value ?? ''}`.toUpperCase()
  if (normalized === 'TICKET') return 'HELPDESK'
  if (normalized === 'PRIMARY') return 'Source'
  if (normalized === 'SECONDARY') return 'Destination'
  return value
}

const ERP_NATURES = ['ERP', 'ERP_SECONDARY']
const getNatureValue = (value: unknown) => String(value ?? '').toUpperCase()
const getTypeValue = (value: unknown) => String(value ?? '').toUpperCase()

type ConnectedStoreSnapshot = {
  fetchedConnectedPlatformData: any[]
  mainStoreData: any
  accountingStoreData: any
  shippingStoreData: any
  erpStoreData: any
  omsStoreData: any
}

function resolveBaseChannelList(snapshot: ConnectedStoreSnapshot) {
  const { fetchedConnectedPlatformData } = snapshot
  const hasPrimary =
    fetchedConnectedPlatformData.some((item) => item.isMainStore === true) ||
    Boolean(snapshot.mainStoreData)
  const hasAccounting =
    fetchedConnectedPlatformData.some((item) => item.isAccountingStore === true) ||
    Boolean(snapshot.accountingStoreData)
  const hasShipping =
    fetchedConnectedPlatformData.some((item) => item.isShippingStore === true) ||
    Boolean(snapshot.shippingStoreData)
  const hasErp =
    fetchedConnectedPlatformData.some((item) => item.isErpStore === true) ||
    Boolean(snapshot.erpStoreData)
  const hasOms =
    fetchedConnectedPlatformData.some((item) => item.isOmsStore === true) ||
    Boolean(snapshot.omsStoreData)

  const knowsConnectedState =
    fetchedConnectedPlatformData.length > 0 ||
    hasPrimary ||
    hasAccounting ||
    hasShipping ||
    hasErp ||
    hasOms

  if (!knowsConnectedState) {
    return {
      channelList: Array.from(
        new Set(['PRIMARY', 'ACCOUNTING', 'SHIPPING', 'ERP', 'TICKET', 'SECONDARY'])
      ),
      preferredNature: 'PRIMARY' as string | undefined,
    }
  }

  const connectedStoreSet = new Set<string>()
  if (hasPrimary) connectedStoreSet.add('PRIMARY')
  if (hasAccounting) connectedStoreSet.add('ACCOUNTING')
  if (hasShipping) connectedStoreSet.add('SHIPPING')
  if (hasErp) connectedStoreSet.add('ERP')
  if (hasOms) connectedStoreSet.add('OMS')

  const storeOrder = ['PRIMARY', 'ACCOUNTING', 'SHIPPING', 'ERP']
  let channelList = storeOrder.filter((tab) => !connectedStoreSet.has(tab))

  if (connectedStoreSet.size > 0) {
    channelList = channelList.length > 0 ? [...channelList, 'SECONDARY'] : ['SECONDARY']
  }

  if (hasOms) {
    channelList = channelList.filter((tab) => tab !== 'PRIMARY')
  }

  if (channelList.length === 0) {
    channelList = hasOms ? ['SECONDARY'] : ['PRIMARY', 'ACCOUNTING', 'SHIPPING', 'ERP']
  }

  const preferredNature =
    connectedStoreSet.size > 0
      ? 'SECONDARY'
      : channelList.length > 0
      ? channelList[0]
      : undefined

  return {
    channelList: Array.from(new Set(channelList)),
    preferredNature,
  }
}

function computeAvailableChannelTabs(
  channel: string[],
  availablePlatforms: any[],
  snapshot: ConnectedStoreSnapshot
) {
  const { fetchedConnectedPlatformData } = snapshot
  const availablePrimary = availablePlatforms.some((platform) => {
    const natureValue = getNatureValue(platform.nature)
    return natureValue === 'PRIMARY' || natureValue === 'BOTH'
  })

  const availableSecondary = availablePlatforms.some((platform) => {
    const natureValue = getNatureValue(platform.nature)
    return (
      natureValue === 'SECONDARY' ||
      natureValue === 'BOTH' ||
      natureValue === 'SHIPPING_SECONDARY' ||
      natureValue === 'ACCOUNTING_SECONDARY'
    )
  })

  const availableAccounting = availablePlatforms.some((platform) => {
    const natureValue = getNatureValue(platform.nature)
    return natureValue === 'ACCOUNTING' || natureValue === 'ACCOUNTING_SECONDARY'
  })

  const availableShipping = availablePlatforms.some((platform) => {
    const natureValue = getNatureValue(platform.nature)
    return natureValue === 'SHIPPING' || natureValue === 'SHIPPING_SECONDARY'
  })

  const availableERP = availablePlatforms.some((platform) =>
    ERP_NATURES.includes(getNatureValue(platform.nature))
  )
  const availableTicketing = availablePlatforms.some(
    (platform) => getTypeValue(platform.type) === 'TICKET'
  )

  let tabs = Array.from(new Set(['ALL', ...channel]))

  const ensureTabRemoved = (label: string) => {
    const index = tabs.indexOf(label)
    if (index > -1) {
      tabs.splice(index, 1)
    }
  }

  const ensureTabPresent = (label: string, position?: number) => {
    const existingIndex = tabs.indexOf(label)
    if (existingIndex > -1) {
      if (position !== undefined && existingIndex !== position) {
        tabs.splice(existingIndex, 1)
        tabs.splice(position, 0, label)
      }
      return
    }
    if (position !== undefined) {
      tabs.splice(position, 0, label)
    } else {
      tabs.push(label)
    }
  }

  if (availableERP) {
    ensureTabPresent('ERP', 1)
  } else {
    ensureTabRemoved('ERP')
  }

  if (availableTicketing) {
    ensureTabPresent('TICKET', tabs.includes('ERP') ? tabs.indexOf('ERP') + 1 : 1)
  } else {
    ensureTabRemoved('TICKET')
  }

  if (!availablePrimary) ensureTabRemoved('PRIMARY')
  if (!availableSecondary) ensureTabRemoved('SECONDARY')
  if (!availableAccounting) ensureTabRemoved('ACCOUNTING')
  if (!availableShipping) ensureTabRemoved('SHIPPING')

  const hasPrimary =
    fetchedConnectedPlatformData.some((item) => item.isMainStore === true) ||
    Boolean(snapshot.mainStoreData)
  const hasAccounting =
    fetchedConnectedPlatformData.some((item) => item.isAccountingStore === true) ||
    Boolean(snapshot.accountingStoreData)
  const hasShipping =
    fetchedConnectedPlatformData.some((item) => item.isShippingStore === true) ||
    Boolean(snapshot.shippingStoreData)
  const hasErp =
    fetchedConnectedPlatformData.some((item) => item.isErpStore === true) ||
    Boolean(snapshot.erpStoreData)
  const hasOms =
    fetchedConnectedPlatformData.some((item) => item.isOmsStore === true) ||
    Boolean(snapshot.omsStoreData)

  if (!hasPrimary && !hasAccounting && !hasShipping && !hasErp && !hasOms) {
    ensureTabRemoved('SECONDARY')
  }

  if (hasOms) {
    ensureTabRemoved('PRIMARY')
  }

  return tabs
}

function resolveNatureFromQuery(
  tabs: string[],
  preferredNature: string | undefined,
  search: string
) {
  if (tabs.length === 0) return ''

  const queryNature = new URLSearchParams(search).get('nature')
  const normalizedQueryNature = queryNature?.toUpperCase()
  const resolvedNature =
    normalizedQueryNature === 'TICKET'
      ? 'TICKET'
      : normalizedQueryNature || preferredNature || tabs[0]

  return tabs.includes(resolvedNature) ? resolvedNature : tabs[0]
}

const ChannelsIntegration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    fetchedConnectedPlatformData,
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
  } = useAppSelector((state) => state.fetchConnectedPlatform)
  const { platformData } = useAppSelector((state) => state.platform)
  const dispatch = useAppDispatch()

  const connectedStoreSnapshot = useMemo<ConnectedStoreSnapshot>(
    () => ({
      fetchedConnectedPlatformData,
      mainStoreData,
      accountingStoreData,
      shippingStoreData,
      erpStoreData,
      omsStoreData,
    }),
    [
      fetchedConnectedPlatformData,
      mainStoreData,
      accountingStoreData,
      shippingStoreData,
      erpStoreData,
      omsStoreData,
    ]
  )

  const [search, setSearch] = useState('');
  const [nature, setNature] = useState(() => {
    const queryNature = new URLSearchParams(window.location.search).get('nature')
    return queryNature?.toUpperCase() || 'PRIMARY'
  });
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);
  const [tabCatalog, setTabCatalog] = useState<any[]>(() => platformData)

  useEffect(() => {
    dispatch(fetchPlatformsAsync()).then((res) => {
      if (Array.isArray(res?.payload?.data) && res.payload.data.length > 0) {
        setTabCatalog(res.payload.data)
      }
    })
  }, [dispatch])

  const { channelList, preferredNature } = useMemo(
    () => resolveBaseChannelList(connectedStoreSnapshot),
    [connectedStoreSnapshot]
  )

  const catalogForTabs = tabCatalog.length > 0 ? tabCatalog : platformData

  const availableChannels = useMemo(() => {
    if (catalogForTabs.length === 0) {
      return Array.from(new Set(['ALL', ...channelList]))
    }
    return computeAvailableChannelTabs(
      channelList,
      catalogForTabs,
      connectedStoreSnapshot
    )
  }, [channelList, catalogForTabs, connectedStoreSnapshot])

  useEffect(() => {
    if (availableChannels.length === 0) {
      setNature('')
      return
    }
    const resolvedNature = resolveNatureFromQuery(
      availableChannels,
      preferredNature,
      location.search
    )
    setNature(resolvedNature)

    if (!location.pathname.startsWith('/platforms')) return

    const queryNature = new URLSearchParams(location.search).get('nature')?.toUpperCase()
    const normalizedQueryNature = queryNature === 'HELPDESK' ? 'TICKET' : queryNature
    if (normalizedQueryNature !== resolvedNature) {
      navigate(`/platforms?nature=${resolvedNature.toLowerCase()}`, { replace: true })
    }
  }, [availableChannels, preferredNature, location.search, location.pathname, navigate])

  useEffect(() => {
    if (!location.pathname.startsWith('/platforms')) return

    const queryNature = new URLSearchParams(location.search).get('nature')?.toUpperCase()
    const hasOms = fetchedConnectedPlatformData?.some((item) => item.isOmsStore === true)

    if (hasOms && queryNature === 'PRIMARY') {
      navigate('/platforms?nature=secondary', {replace: true})
    }
  }, [location.pathname, location.search, fetchedConnectedPlatformData, navigate])

  const tabs = [
    { label: 'Active Integrations', path: '/listing-channels' },
    { label: 'Available Integrations', path: '/platforms' },
  ];

  const activeTabIndex = location.pathname === '/platforms' ? 1 : 0;

  const handleTabClick = (index: number) => {
        if (index === 1) {
          if (!nature) {
            navigate(tabs[index].path);
            return;
          }
          navigate(tabs[index].path + `?nature=${nature?.toLowerCase()}`);
        } else {
          navigate(tabs[index].path)
        }
  };

  const handleNatureFilter = (type: string) => {
    setNature(type);
    if (location.pathname === '/platforms') {
      navigate(`/platforms?nature=${type.toLowerCase()}`);
    }
  };

  useEffect(() => {
    if (!location.pathname.startsWith('/platforms')) {
      setShowEmptyMessage(false)
      return
    }

    if (!search.trim()) {
      setShowEmptyMessage(false)
      return
    }

    const timer = setTimeout(() => {
      setShowEmptyMessage(true)
    }, 150)

    return () => clearTimeout(timer)
  }, [search, location.pathname])

  return (
    <div className='card mt-3'>
      <div className='custom-card-header'>
        <div className='tabs-header'>
          {tabs.map((tab, index) => (
            <div
              key={index}
              className={`tab ${activeTabIndex === index ? 'active' : ''}`}
              onClick={() => handleTabClick(index)}
            >
              {tab.label}
              <div className={`underline ${activeTabIndex === index ? 'active' : ''}`} />
            </div>
          ))}
        </div>
      </div>

      <div className='card-body'>
        {activeTabIndex === 1 && (
          <div className='d-flex justify-content-between'>
            <div className='space-x-4'>
              {availableChannels.map((type) => (
                <button
                  key={type}
                  className={`btn-nature ${nature === type ? 'active' : ''}`}
                  onClick={() => {
                    setSearch('')
                    handleNatureFilter(type)
                  }}
                >
                  {getChannelDisplayLabel(type)}
                </button>
              ))}
            </div>
            <div className='position-relative w-350px'>
              <span className='search-icon'>
                <i className='fas fa-search' />
              </span>
              <input
                className='form-control ps-5 py-2 text-gray mr-sm-2 input-sm'
                type='search'
                placeholder='Search Channels'
                onChange={(e) => setSearch(e.target.value)}
                value={search}
              />
            </div>
          </div>
        )}

        {location.pathname.startsWith('/listing-channels') && (
          <ConnectedPlatforms search={search} nature={nature} />
        )}

        {location.pathname.startsWith('/platforms') &&
          (nature ? (
            <Platforms search={search} nature={nature} channels={availableChannels} />
          ) : null)}
      </div>
    </div>
  )
};

export default ChannelsIntegration;
